import Form, { IChangeEvent } from "@rjsf/core";
import { Button, Panel, theme } from "@xometry/xometry_loft";
import _ from "lodash";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import ProposalJsonForm from "../JsonSchemaLoftTheme";
import {
  GetMarketplaceFormByIdQuery,
  GetMarketplaceFormByIdQueryResult,
} from "../../schema/generated/schema";
import {
  FormSection,
  FormContainer,
  ButtonsContainer,
  PanelCss,
  FormWrapperCss,
} from "./SingleFormView.style";
import { useConfig } from "../proposals/contexts/configContext";
import { JsonValue } from "../FormStepper/FormStepper";

export const SingleFormView: React.FC = () => {
  const [formInputData, setFormInputData] = useState<
    Array<{ key: string; label: string; value: any }>
  >([]);
  const formRef = useRef<Form<any>>();
  const [touchAllErrors, setTouchAllErrors] = useState<boolean>(false);
  const [submissionInProgress, setSubmissionInProgress] =
    useState<boolean>(false);

  // both of these constants are used to get the id of the form
  // singleForm is used when consuming the component, gets passed as a prop
  const { singleForm } = useConfig();
  // formIdFromParams is used for testing, fetched from URL params
  const { formId: formIdFromParams } = useParams<{ formId: string }>();

  const formData = useQuery<GetMarketplaceFormByIdQueryResult>(
    GetMarketplaceFormByIdQuery,
    {
      variables: {
        formId: singleForm?.id || formIdFromParams,
      },
    }
  );

  const form = useMemo(() => {
    return formData?.data?.getMarketplaceFormById;
  }, [formData]);

  const isFormValid = (formData: any, formRef: Form<any>): boolean => {
    return formRef.validate(formData).errors.length === 0;
  };

  function convertArrayToKeyValueObject(
    inputArray: { key: string; label: string; value: any }[]
  ): { [key: string]: JsonValue } {
    const result: { [key: string]: JsonValue } = {};
    inputArray.forEach(item => {
      result[item.key] = item.value;
    });
    return result;
  }

  return !form ? (
    <></>
  ) : (
    <FormSection>
      <FormContainer>
        <Panel css={PanelCss} kind="primary">
          <div css={FormWrapperCss}>
            <ProposalJsonForm
              id={form.versionId}
              schema={_.omit(form.formDataSchema, ["title", "description"])}
              ref={ref => {
                if (ref) {
                  formRef.current = ref;
                }
              }}
              touchAllErrors={touchAllErrors}
              onChange={(e: IChangeEvent<any>) => {
                if (e.schema.properties) {
                  const transformedFormData = Object.entries(
                    e.schema.properties
                  ).map(([key, value]) => {
                    return {
                      key,
                      // eslint-disable-next-line dot-notation
                      label: value["title"] as string,
                      value: e.formData[key],
                    };
                  });
                  setFormInputData(transformedFormData);
                }
              }}
              elements={form.config.elements}
              elementArrangement={form.config.elementArrangement}
              canEdit
              isSeller={false}
            />
            <ButtonsContainer>
              <Button
                kind={theme.kind.neutral}
                onClick={() => singleForm?.onClose()}
              >
                Cancel
              </Button>
              <Button
                kind={theme.kind.primarySolid}
                disabled={submissionInProgress}
                scale="large"
                onClick={async () => {
                  if (
                    formRef.current &&
                    !isFormValid(
                      convertArrayToKeyValueObject(formInputData),
                      formRef.current
                    )
                  ) {
                    setTouchAllErrors(true);
                    return;
                  }
                  setSubmissionInProgress(true);
                  const response = await singleForm?.onSubmit(formInputData);
                  if (!response) {
                    // TODO display a proper error message here
                    console.log("There was an error");
                  }
                  setSubmissionInProgress(false);
                }}
              >
                Submit
              </Button>
            </ButtonsContainer>
          </div>
        </Panel>
      </FormContainer>
    </FormSection>
  );
};

export default SingleFormView;
